<template>
    <div v-loading="busy">
        <div class="page-header d-flex align-items-center">
            <div class="page-title flex-grow-1">
                <h1>CAMPAIGN SCHEDULING</h1>
            </div>
            <div class="page-header-controls">
                <el-tooltip content="Generate Messages">
                    <button
                            class="btn btn-secondary"
                            @click="confirmGenerating"
                            :disabled="!campaignsExist"
                    >
                        <i class="fas fa-cloud-rain"></i>
                    </button>
                </el-tooltip>
                <el-tooltip content="Schedule Sending">
                    <button
                            class="btn btn-danger"
                            @click="confirmScheduling"
                            :disabled="!canSchedule"
                    >
                        <i class="fas fa-play"></i>
                    </button>
                </el-tooltip>
            </div>
        </div>
        <div class="row">
            <div class="col-4">
                <div class="border border-gray-500 campaign-list-container">
                    <div class="py-2 bg-gray-500 text-center header">
                        <h6 class="mb-0">CAMPAIGNS</h6>
                    </div>
                    <auto-height :margin="10" class="campaign-list d-flex">
                        <placeholder-no-campaigns
                                v-if="!campaignsExist"
                                class="text-secondary"
                                :url="campaignIndexLink"
                        ></placeholder-no-campaigns>
                        <campaign-list
                                v-else
                                class="flex-fill"
                                :items="campaigns"
                                :selected.sync="selectedCampaign"
                        ></campaign-list>
                    </auto-height>
                </div>
            </div>
            <div class="col-8">
                <div class="border border-gray-500 message-list-container">
                    <div class="py-2 bg-gray-500 text-center header">
                        <h6 class="mb-0">MESSAGES</h6>
                    </div>
                    <auto-height :margin="10" class="message-list d-flex">
                        <placeholder
                                v-if="!campaignsExist"
                                class="text-secondary"
                        ></placeholder>
                        <placeholder
                                v-else-if="!hasSelectedCampaign"
                                class="text-secondary"
                                message="Select a campaign to view messages"
                        ></placeholder>
                        <message-list
                                v-else
                                :key="messageListKey"
                                :campaign="selectedCampaign"
                                ref="messageList"
                                @cached-preview-checked="onCachedPreviewChecked"
                        >
                            <template v-slot:not-generated>
                                <placeholder-messages-not-generated
                                        class="text-secondary"
                                        @generate="confirmGenerating"
                                ></placeholder-messages-not-generated>
                            </template>
                        </message-list>
                    </auto-height>
                </div>
            </div>
        </div>
        <dialog-contact-form-wrapper></dialog-contact-form-wrapper>
    </div>
</template>

<script>
  import CampaignList from './campaign-list';
  import MessageList from './message-list';
  import Placeholder from './placeholder';
  import PlaceholderNoCampaigns from './placeholder-no-campaigns';
  import PlaceholderMessagesNotGenerated from './placeholder-messages-not-generated';
  import AutoHeight from '../../components/auto-height';
  import DialogContactFormWrapper from './dialog-contact-form-wrapper';

  export default {
    components: {
      PlaceholderNoCampaigns,
      CampaignList,
      MessageList,
      Placeholder,
      PlaceholderMessagesNotGenerated,
      AutoHeight,
      DialogContactFormWrapper,
    },

    props: {
      initialCampaigns: Array,
      initialHasCachedPreview: Boolean,
      campaignIndexLink: String,
      generatePreviewUrl: String,
      scheduleUrl: String,
    },

    data() {
      return {
        campaigns: [...this.initialCampaigns],
        messages: undefined,
        selectedCampaign: null,
        busy: false,
        hasCachedPreview: this.initialHasCachedPreview,
        messageListKey: null,
      };
    },

    computed: {
      /**
       * @return {boolean}
       */
      campaignsExist() {
        return !_.isEmpty(this.campaigns);
      },

      /**
       * @return {boolean}
       */
      hasSelectedCampaign() {
        return Boolean(this.selectedCampaign);
      },

      /**
       * @return {boolean}
       */
      canSchedule() {
        return this.campaignsExist && this.hasCachedPreview;
      },
    },

    watch: {
      selectedCampaign(val) {
        this.messageListKey = _.get(val, 'model.id', '') + '_' + moment().unix()
      },
    },

    methods: {
      /**
       * @return {undefined}
       */
      confirmScheduling() {
        this.$confirm('Are you sure you want to schedule messages sending?', {
          type: 'warning',
        })
          .then(this.schedule)
          .catch(() => {
            //
          });
      },

      schedule() {
        this.busy = true;
        axios.post(this.scheduleUrl, {}, {})
          .then(({data: {message, campaigns}}) => {
            this.$message.success(message || 'Successfully scheduled');
            this.campaigns = campaigns;
          })
          .catch(({response: {data: {message}}}) => {
            this.$message.error(message || 'An error raised during messages scheduling');
          })
          .then(() => this.busy = false);
      },

      /**
       * @return {undefined}
       */
      confirmGenerating() {
        this.$confirm('Are you sure you want to generate messages?', {
          type: 'warning',
        })
          .then(this.generate)
          .catch(() => {
            //
          });
      },

      /**
       * @return {undefined}
       */
      generate() {
        this.busy = true;
        axios.post(this.generatePreviewUrl, {}, {})
          .then(({data: {message}}) => {
            this.$message.success(message || 'Successfully generated');
            this.refreshMessageList();
            this.hasCachedPreview = true;
          })
          .catch(({response: {data: {message}}}) => {
            this.$message.error(message || 'An error raised during preview generation');
          })
          .then(() => this.busy = false);
      },

      /**
       * @return {undefined}
       */
      refreshMessageList() {
        this.hasSelectedCampaign && this.$refs.messageList.refresh();
      },

      /**
       * @param {boolean} value
       */
      onCachedPreviewChecked(value) {
        this.hasCachedPreview = value;
      }
    },

    created() {
      eventBus.$on('refresh-message-list-items', this.refreshMessageList);
    },
    beforeDestroy() {
      eventBus.$off('refresh-message-list-items', this.refreshMessageList);
    }
  }
</script>
